import React from 'react'
import { Text, csx, useBreakpoint } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { useIntl } from 'react-intl'

import type { AssemblyItemsList_ProductSkuAssemblyItem$key } from './__generated__/AssemblyItemsList_ProductSkuAssemblyItem.graphql'
import { useAssemblyItemsContext } from './context/AssemblyItemsContext'
import { CheckoutButton } from '../../components'
import { AssemblyItemsFooter } from './AssemblyItemsFooter'
import { AssemblyItemsListGroup } from './AssemblyItemsListGroup'

type AssemblyItemsListProps = {
  productSkuAssemblyItem: AssemblyItemsList_ProductSkuAssemblyItem$key
  handleAddAssemblyItemToCart: () => Promise<void>
  handleNextAssemblyItem: () => void
  isBusy: boolean
  required: boolean
  hasMoreOneAssemblyItem: boolean | undefined
}

export const AssemblyItemsList = ({
  productSkuAssemblyItem,
  handleAddAssemblyItemToCart,
  handleNextAssemblyItem,
  hasMoreOneAssemblyItem,
  isBusy,
  required,
}: AssemblyItemsListProps) => {
  const { assemblyItemState } = useAssemblyItemsContext()
  const { breakpoint } = useBreakpoint()
  const isMobile = breakpoint === 'mobile'
  const assemblyItem = useFragment<AssemblyItemsList_ProductSkuAssemblyItem$key>(
    graphql`
      fragment AssemblyItemsList_ProductSkuAssemblyItem on ProductSkuAssemblyItem {
        id
        displayName
        composition {
          minQuantity
          ...AssemblyItemsListGroup_ProductSkuAssemblyItemComposition
        }
      }
    `,
    productSkuAssemblyItem
  )

  const { formatMessage } = useIntl()

  const shouldButtonDisabled = () => {
    if (isBusy) return true

    if (required) {
      return !assemblyItemState.values.some(
        (value) => value.assemblyId === assemblyItem.id
      )
    }

    return (
      assemblyItem.composition.minQuantity > 0 &&
      !assemblyItemState.values.some(
        (value) => value.assemblyId === assemblyItem.id
      )
    )
  }

  return (
    <>
      <Text variant="display">{assemblyItem?.displayName}</Text>
      <AssemblyItemsListGroup
        currentAssemblyItemId={assemblyItem.id}
        assemblyItemComposition={assemblyItem.composition}
        displayName={assemblyItem.displayName}
      />
      <div />
      <AssemblyItemsFooter>
        <CheckoutButton
          data-testid="next-assembly-item-or-add-assembly-to-cart-button"
          disabled={shouldButtonDisabled()}
          loading={isBusy}
          size="large"
          className={csx({
            width: isMobile ? 'full' : '20rem',
            float: 'right',
          })}
          onClick={
            hasMoreOneAssemblyItem
              ? handleNextAssemblyItem
              : handleAddAssemblyItemToCart
          }
        >
          {formatMessage({
            id: hasMoreOneAssemblyItem ? 'next' : 'continueToCart',
          })}
        </CheckoutButton>
      </AssemblyItemsFooter>
    </>
  )
}
